import { Controller } from "@hotwired/stimulus"

// Wires the Files app search box to Files::SemanticSearch (content search,
// not just filename matching) via GET /apps/files/search. Debounced as the
// user types, plus Enter-to-search-now; clearing the box restores the normal
// folder/file grid ("browse" target) in place.
//
// FILES_DESIGN_SPEC.md principle 2 ("results show why they matched"): every
// result row carries a content snippet from the matching UserFileChunk (see
// UserFilesController#search_result_payload), and a summary line up top
// ("3 files mention "…" - matches highlighted from inside the documents.")
// with the query term itself <mark>ed inside each snippet.
export default class extends Controller {
    static targets = ["input", "results", "browse", "clear"]
    static values = { url: String }

    connect() {
        this.timer = null
    }

    disconnect() {
        window.clearTimeout(this.timer)
    }

    queueSearch() {
        window.clearTimeout(this.timer)
        this.timer = window.setTimeout(() => this.searchNow(), 350)
    }

    searchNow(event) {
        event?.preventDefault()
        window.clearTimeout(this.timer)
        const query = this.inputTarget.value.trim()
        if (this.hasClearTarget) this.clearTarget.hidden = query.length === 0
        if (!query) return this.clear()

        fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`, {
            headers: { Accept: "application/json" },
            credentials: "same-origin"
        })
            .then((response) => response.json())
            .then((data) => this.renderResults(query, (data && data.results) || [], data && data.summary))
            .catch(() => this.renderResults(query, [], null))
    }

    clear() {
        this.inputTarget.value = ""
        if (this.hasClearTarget) this.clearTarget.hidden = true
        this.resultsTarget.hidden = true
        this.resultsTarget.innerHTML = ""
        if (this.hasBrowseTarget) this.browseTarget.hidden = false
    }

    renderResults(query, results, summary) {
        if (this.hasBrowseTarget) this.browseTarget.hidden = true
        this.resultsTarget.hidden = false
        this.resultsTarget.innerHTML = ""

        if (results.length === 0) {
            const empty = document.createElement("p")
            empty.className = "files-search-empty"
            empty.textContent = `No files match "${query}".`
            this.resultsTarget.appendChild(empty)
            return
        }

        if (summary) {
            const summaryEl = document.createElement("p")
            summaryEl.className = "files-search-summary"
            summaryEl.textContent = summary
            this.resultsTarget.appendChild(summaryEl)
        }

        results.forEach((result) => {
            const item = document.createElement("a")
            item.className = "files-search-result"
            item.href = result.folder_url
            item.dataset.turbo = "false"

            const name = document.createElement("span")
            name.className = "files-search-result-name"
            name.textContent = result.name

            const snippet = document.createElement("p")
            snippet.className = "files-search-result-snippet"
            this._renderHighlighted(snippet, result.snippet, query)

            item.appendChild(name)
            item.appendChild(snippet)
            this.resultsTarget.appendChild(item)
        })
    }

    // Wraps every case-insensitive occurrence of `query` in the snippet with
    // <mark>, built from text nodes (never innerHTML on the snippet itself)
    // so a query containing HTML-special characters can never inject markup.
    _renderHighlighted(el, text, query) {
        const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        const pattern = new RegExp(`(${escaped})`, "ig")
        const parts = String(text).split(pattern)

        parts.forEach((part, index) => {
            if (index % 2 === 1) {
                const mark = document.createElement("mark")
                mark.textContent = part
                el.appendChild(mark)
            } else if (part.length) {
                el.appendChild(document.createTextNode(part))
            }
        })
    }
}
